// src/pages/billing/CheckoutSuccessPage.js
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

const MAX_TRIES = 8;
const POLL_INTERVAL_MS = 1500;

const CheckoutSuccessPage = () => {
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get('session_id');
    const { refreshEntitlements } = useAuth();

    const [status, setStatus] = useState('polling'); // 'polling' | 'success' | 'timeout'
    const pollCountRef = useRef(0);
    const timerRef = useRef(null);

    useEffect(() => {
        const poll = async () => {
            pollCountRef.current += 1;

            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/get_entitlements.php`, {
                    method: 'GET',
                    credentials: 'include',
                    headers: { 'Accept': 'application/json' },
                });

                if (response.ok) {
                    const result = await response.json();
                    if (result.status === 'success' && result.is_premium) {
                        if (refreshEntitlements) await refreshEntitlements();
                        setStatus('success');
                        return; // Stop polling
                    }
                }
            } catch (error) {
                console.error('Success page poll error:', error);
            }

            if (pollCountRef.current >= MAX_TRIES) {
                setStatus('timeout');
                return;
            }

            timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
        };

        // Start polling immediately
        timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [refreshEntitlements]);

    if (status === 'polling') {
        return (
            <div className="p-8 md:p-12 text-white text-center">
                <div className="max-w-md mx-auto">
                    <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-6" />
                    <h1 className="text-2xl font-bold mb-2">Activating your Premium access...</h1>
                    <p className="text-neutral-400 text-sm">This usually takes just a moment.</p>
                    {sessionId && (
                        <p className="text-xs text-neutral-600 mt-4">Session: {sessionId}</p>
                    )}
                </div>
            </div>
        );
    }

    if (status === 'timeout') {
        return (
            <div className="p-8 md:p-12 text-white text-center">
                <div className="max-w-md mx-auto">
                    <h1 className="text-2xl font-bold mb-3">Payment received!</h1>
                    <p className="text-neutral-400 mb-6">
                        Your payment was successful. Premium access may take a few minutes to activate.
                        Please refresh the page or check back shortly.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <button
                            onClick={() => window.location.reload()}
                            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-md transition-colors"
                        >
                            Refresh Page
                        </button>
                        <Link
                            to="/settings/profile"
                            className="border border-neutral-600 hover:border-neutral-400 text-neutral-300 hover:text-white font-semibold py-2 px-6 rounded-md transition-colors"
                        >
                            Go to Profile
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    // status === 'success'
    return (
        <div className="p-8 md:p-12 text-white text-center">
            <div className="max-w-md mx-auto">
                <CheckCircleIcon className="w-16 h-16 text-green-400 mx-auto mb-4" />
                <h1 className="text-3xl font-bold mb-3">You're Premium!</h1>
                <p className="text-neutral-400 mb-8">
                    Your account has been upgraded. Enjoy unlimited AI optimizations and resume downloads.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Link
                        to="/resume"
                        className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-md transition-colors"
                    >
                        Go to Resume Builder
                    </Link>
                    <Link
                        to="/settings/profile"
                        className="border border-neutral-600 hover:border-neutral-400 text-neutral-300 hover:text-white font-semibold py-2 px-6 rounded-md transition-colors"
                    >
                        View Profile
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default CheckoutSuccessPage;
